import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { ocplcContainerStyles } from '../DashboardHelpers';
import Box from '@mui/material/Box';
import { filterOcplcDashboard, ocplcgetTOFAuditTrial, countryList, getOcplcBusinessModels, loadCountForDT, categoryFilter, productGroupDropDown } from 'actions/ocplcAction';
import EditOcplc from 'pages/OcplcEditDashboard/CopyEditAudit/EditOcplc';
import ActionableButtons from './ActionableButtons';
import { DEFAULT_LOC, FACTORY_SERVICES, LOC_AVAILABILITY, MATERIAL, MATERIAL_AVAILABILITY, MODEL_DT, OCPLC_ADMIN, REGIONS, SERVICE, SPINNER_MESSAGE, VERSION_0003 } from 'constants/ocplcConstants';
import FeedDialog from 'pages/OcplcEditDashboard/CopyEditAudit/FeedTOFAudit';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { ButtonSpinner } from 'components/ButtonSpinner';
import { handleUnauthorizedAccess, isUserDisabled } from 'pages/Utils/OcplcUtil';
import TableGrid from 'pages/TableDataGrid';
import { tableConfigMap } from './TableDataConfig';
import { snackBar } from 'actions/modelActions';

const PlcEditDashboard = (props) => {
    const dispatch = useDispatch();
    const { version, region, kmatId, regionDropdown, productGroup, userEmail, assignedRole, productLineID, checkedRows, setCheckedRows, ocplcAdmin, selectedTable, selectedCategory, filterKmats, categories, selectedProductGroup, filterOcplcKmatForPG, selectedIds, setSelectedIds } = props;
    
    const tableConfig = tableConfigMap[selectedTable];
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [feedRowData, setFeedRowData] = useState(null);
    const [openEditPopup, setOpenEditPopup] = useState(false);
    const [openFeedDialog, setOpenFeedDialog] = useState(false);
    const [lockModel, setLockModel] = useState({ open: false, message: "" });
    const disableAction = version === VERSION_0003 ? true : false;
    const isLocalizationMode = selectedTable === DEFAULT_LOC || selectedTable === LOC_AVAILABILITY;
    const { filtersEdit, businessModels, isLoading, countryListData } = useSelector(state => state.ocplc);
    const params = tableConfig.getParams({ version, region, kmatId, productGroup, productLineID, selectedCategory, selectedProductGroup });

    const { fullData: tableData, filteredData: filteredData } = useSelector(tableConfig.selector);
    const newCountryList = [{ "countryCode": "ALL", "countryName": "ALL", "regionCode": REGIONS, }, ...(countryListData ?? [])]

    useEffect(() => {
        if (!isLocalizationMode) {
        dispatch(getOcplcBusinessModels());
        }
    }, []);

    useEffect(() => {
        setCheckedRows([]);
        setSelectedIds([]);
        dispatch(countryList(region));
        tableConfig.loadData(dispatch, params);
        if (selectedTable === MATERIAL_AVAILABILITY) {
            const isMaterial = selectedTable === MATERIAL_AVAILABILITY;
            const tableName = isMaterial ? MATERIAL : '';
            dispatch(categoryFilter(version, region, tableName));
        }
    }, [version, region, kmatId, selectedTable, selectedCategory, selectedProductGroup]);

    const handleRowSelection = useCallback((newSelection) => {
        setSelectedIds((prevSelected) => {
            const merged = new Set(prevSelected);
            newSelection.forEach(id => merged.add(id));
            const visibleIds = Array.isArray(filteredData)
                ? filteredData.map(row => row.pkId)
                : [];
            visibleIds.forEach(id => {
                if (!newSelection.includes(id)) {
                    merged.delete(id);
                }
            });
            return Array.from(merged);
        });
    }, [filteredData]);
 
    // Sync checkedRows with selectedIds and filteredData
    useEffect(() => {
        if (!tableData || !Array.isArray(tableData)) {
            setCheckedRows([]);
            return;
        }
        const dataMap = new Map(tableData.map(row => [row.pkId, row]));
        const selectedItems = selectedIds.map(id => dataMap.get(id)).filter(Boolean);
        setCheckedRows(selectedItems);
    }, [tableData, selectedIds]);
 

    const handleFilterChange = useCallback((col, value) => {
        dispatch(filterOcplcDashboard({ ...col, searchText: value }))
    }, [dispatch]);

    const regionLock = true
    
    const handleViewModel = (row) => {
        const isNoChoice = row?.choiceId?.startsWith("NOCHOICE");
        if (row?.regionLock && isNoChoice) {
            dispatch(snackBar(true, `The world-wide record is locked by ${row?.whoChanged}, It only allow to edit after unlocked.`));
        }
        else if (disableAction || isUserDisabled(assignedRole, selectedTable) || !(handleUnauthorizedAccess(userEmail, row.whoChanged, setLockModel, row.status, filterKmats, row.productFamily,assignedRole))) {
            setOpenEditPopup(true);
            setSelectedRowData(row);
        }
    };
    
    const handleFeedDialog = useCallback((row, feedType) => {
        setOpenFeedDialog(true);
        setFeedRowData(feedType);
        dispatch(ocplcgetTOFAuditTrial(feedType, row));
    }, []);

    const handleRowClick = (params, event) => {
        if (event.target.type !== "checkbox") {
            event.defaultMuiPrevented = true;
            handleViewModel(params.row);
        }
    };

    const handleDialogClose = useCallback(() => {
        setLockModel({ open: false, message: "" });
    }, []);

    const columns = tableConfig.getColumns(filtersEdit, tableData, handleFilterChange, newCountryList, businessModels, regionDropdown, handleViewModel, region, disableAction, handleFeedDialog,selectedTable);

    return (
        <Box sx={{ ...ocplcContainerStyles, minWidth: "100%" }}>
            <>
                <ActionableButtons
                    setCheckedRow={setCheckedRows}
                    countryList={newCountryList}
                    assignedRole={assignedRole}
                    version={version}
                    productGroup={productGroup}
                    kmatId={kmatId}
                    region={region}
                    disableAction={disableAction}
                    filteredModelData={filteredData}
                    checkedRow={checkedRows}
                    userEmail={userEmail}
                    businessModels={businessModels}
                    productLineID={productLineID}
                    tableConfig={tableConfig}
                    ocplcAdmin={ocplcAdmin}
                    setLockModel={setLockModel}
                    selectedTable={selectedTable}
                    filterKmats={filterKmats}
                    categories={categories}
                    selectedCategory={selectedCategory}
                    filterOcplcKmatForPG={filterOcplcKmatForPG}
                    selectedProductGroup={selectedProductGroup}
                    regionDropdown={regionDropdown}
                    setSelectedIds={setSelectedIds}
                />
                {isLoading ? (<ButtonSpinner/> ) : (
                    <TableGrid
                        columns={columns}
                        rows={filteredData}
                        tableData={tableData}
                        isLoading={isLoading}
                        checkedRows={checkedRows}
                        onRowClick={handleRowClick}
                        rowSelectionModel={selectedIds}
                        onRowSelectionModelChange={handleRowSelection}
                    />
                )}
            </>

            {openEditPopup && selectedRowData && (
                <EditOcplc
                    open={openEditPopup}
                    handleClose={() => setOpenEditPopup(false)}
                    plcDetails={selectedRowData}
                    setCheckedRows={setCheckedRows}
                    onSave={() => setOpenEditPopup(false)}
                    countryList={newCountryList}
                    businessModels={businessModels}
                    userEmail={userEmail}
                    selectedTableRegion={region}
                    disabledAction={disableAction}
                    assignedRole={assignedRole}
                    kmatId={kmatId}
                    productLineID={productLineID}
                    selectedTable={selectedTable}
                    copy={false}
                    selectedCategory={selectedCategory}
                    productGroupDashboard={productGroup}
                    setSelectedIds={setSelectedIds}
                />
            )}

            {openFeedDialog && (
                <FeedDialog
                    open={openFeedDialog}
                    setOpenFeedDialog={setOpenFeedDialog}
                    feedRowData={feedRowData}
                    selectedTable={selectedTable}
                />
            )}

            {lockModel.open && (
                <ConfirmationDialog
                    open={lockModel.open}
                    onClose={handleDialogClose}
                    content={lockModel.message}
                />
            )}
        </Box>
    )
}

export default PlcEditDashboard;